import React, { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { EditOutlined } from '@ant-design/icons';
import { useDispatch, useModel } from 'umi';
import get from 'lodash/get';
import classNames from 'classnames';

import { hasAccess } from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import { useLeadCardClick } from '@/components/Kanban/LeadCardContext';
import styles from './index.less';
import { LeadModel } from '@/typings/models/Lead';
import { Typography } from 'antd';

const {Paragraph} = Typography
type IProps = {
  editable?: boolean,
  readonly?: boolean,
  isTable?: boolean,
  isCustomer?: boolean,
  property?: string,
  className?: string,
  lead: LeadModel,
  children?: React.ReactNode
}

const TipTapEditableField = ({
  children,
  editable = true,
  readonly = false,
  lead,
  property,
  isTable = false,
  isCustomer = false,
  className = ""
}: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { initialState } = useModel('@@initialState');
  const { setShouldIgnoreClick } = useLeadCardClick();
  const hasEditFieldAccess = hasAccess(initialState.currentUser, [PERMISSIONS.Leads.EDIT, PERMISSIONS.BUSINESS_PARTNER]) && !readonly;
  const dispatch = useDispatch();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure for inline editing - disable block elements that would make it too complex
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // Keep basic formatting
        bold: {},
        italic: {},
        strike: {},
        bulletList: {},
        orderedList: {},
      }),
    ],
    content: get(lead, property) || '',
    editable: false, // Start as read-only
    onBlur: ({ editor }) => {
      handleSave(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: classNames(styles.fieldContainer, {
          [styles.editingField]: isEditing,
          [styles.fieldInTable]: isTable
        }),
        style: 'border: none; outline: none; padding: 4px 8px; min-height: 32px;'
      },
    } as any,
  });

  useEffect(() => {
    setShouldIgnoreClick(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (editor && !isEditing) {
      // Update editor content when lead data changes
      const currentValue = get(lead, property) || '';
      if (editor.getHTML() !== currentValue) {
        editor.commands.setContent(currentValue);
      }
    }
  }, [editor, lead, property, isEditing]);

  // A dotted `customer.*` property always targets the customer model, even if
  // the caller forgot to pass `isCustomer` (e.g. LeadNote rendered as a lead
  // property). Deriving it here prevents saving customer fields into the lead.
  const isCustomerField = isCustomer || !!property?.startsWith('customer.');

  const handleSave = (value: string) => {
    setIsEditing(false);
    editor?.setEditable(false);

    if (!lead || !property) return;

    if (isCustomerField) {
      const customerProp = property?.split(".")?.[1];
      if (!customerProp) return;
      dispatch({
        type: 'customers/change',
        payload: {
          customerId: lead?.customer?.id,
          [customerProp]: value,
        },
      });
    } else if (value !== get(lead, property)) {
      dispatch({
        type: 'leads/edit',
        payload: {
          leadId: lead.id,
          [property]: value
        }
      });
    }
  };

  const handleFieldClick = () => {
    if (hasEditFieldAccess) {
      setIsEditing(true);
      editor?.setEditable(true);
      // Focus the editor after a brief delay to ensure it's ready
      setTimeout(() => {
        editor?.commands.focus();
      }, 100);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsEditing(false);
      editor?.setEditable(false);
      // Reset content to original value
      editor?.commands.setContent(get(lead, property) || '');
    }
  };

  // Handle early returns after all hooks have been called
  if (!editable) {
    return (
      <div className={className} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    );
  }

  if (!editor) {
    return (
      <div className={className} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={classNames(className, styles.editableTextWrapper, styles.fieldContainer, {
        [styles.fieldInTable]: isTable
      })}
      onClick={e => {
        e.stopPropagation();
        if (!isEditing) handleFieldClick();
      }}
      onKeyDown={handleKeyDown}
    >
      {isEditing ? (
        <div style={{ position: 'relative' }}>
          <EditorContent
            editor={editor}
            onClick={e => e.stopPropagation()}
          />
        </div>
      ) : (
        <>
          <div
            className={"tiptap ProseMirror " + (isTable ? `${styles.fieldInTable} mini` : "")}
            dangerouslySetInnerHTML={{ __html: editor.getHTML() || children as string }}
          />
          {hasEditFieldAccess && (
            <EditOutlined
              className={styles.editIcon}
              onClick={(e) => {
                e.stopPropagation();
                handleFieldClick();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TipTapEditableField;
